import { useState, useId } from 'react';

export type TommyPose =
  | 'happy'
  | 'thinking'
  | 'excited'
  | 'studying'
  | 'teaching'
  | 'idea'
  | 'cheerful'
  | 'focused'
  | 'proud'
  | 'waving';

export type TommyAnimation =
  | 'bob'
  | 'float'
  | 'bounce'
  | 'wave'
  | 'celebrate'
  | 'glow'
  | 'wiggle'
  | 'none';

export type TommySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'hero' | number;

const sizeMap: Record<string, { width: number; height: number; class: string }> = {
  xs: { width: 40, height: 40, class: 'w-10 h-10' },
  sm: { width: 60, height: 60, class: 'w-14 h-14' },
  md: { width: 88, height: 88, class: 'w-22 h-22' },
  lg: { width: 120, height: 120, class: 'w-30 h-30' },
  xl: { width: 160, height: 160, class: 'w-40 h-40' },
  '2xl': { width: 220, height: 220, class: 'w-56 h-56' },
  hero: { width: 320, height: 280, class: 'w-72 sm:w-80 h-auto' },
};

const poseAssets: Record<TommyPose, string> = {
  happy: '/mascot/tommy-happy.png',
  thinking: '/mascot/tommy-thinking.png',
  excited: '/mascot/tommy-excited.png',
  studying: '/mascot/tommy-studying.png',
  teaching: '/mascot/tommy-teaching.png',
  idea: '/mascot/tommy-idea.png',
  cheerful: '/mascot/tommy-cheerful.png',
  focused: '/mascot/tommy-focused.png',
  proud: '/mascot/tommy-proud.png',
  waving: '/mascot/tommy-waving.png',
};

export const TOMMY_STUDY_TIPS: string[] = [
  "Tip: Explaining a topic out loud to someone else boosts retention by 50%!",
  "Taking a quick 2-minute break every 25 minutes keeps your focus razor-sharp.",
  "Turn tough lecture notes into a quiz — active recall beats passive re-reading every time!",
  "Small daily progress adds up to massive exam confidence. Keep going!",
  "Stuck on a tricky concept? Ask Socratic Tutor to break it down one step at a time.",
  "Spaced repetition is your superpower. Review flashcards today to lock in memories for months!",
  "You're building real knowledge brick by brick. Tommy is proud of you! ⭐",
];

export const TOMMY_ENCOURAGEMENTS: string[] = [
  "Let's crush this study session together!",
  "Ready to learn something awesome today?",
  "Every question you practice makes you sharper!",
  "You've got this! One concept at a time.",
  "High five! Your brain is doing great work! 🙌",
];

// Lightweight web audio sound synthesizer (pure browser API, zero dependencies)
function playCutePopSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.12);

    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.13);
  } catch {
    // Ignore audio context autoplay restrictions safely
  }
}

export interface TommyMascotProps {
  pose?: TommyPose;
  size?: TommySize;
  animation?: TommyAnimation;
  speechBubble?: React.ReactNode;
  bubblePosition?: 'top' | 'top-right' | 'top-left' | 'right' | 'left' | 'bottom';
  interactive?: boolean;
  withShadow?: boolean;
  soundEnabled?: boolean;
  className?: string;
  onClick?: () => void;
  alt?: string;
}

export default function TommyMascot({
  pose = 'happy',
  size = 'md',
  animation,
  speechBubble,
  bubblePosition = 'top',
  interactive = false,
  withShadow = true,
  soundEnabled = false,
  className = '',
  onClick,
  alt = 'Tommy - Your friendly Learnex learning buddy',
}: TommyMascotProps) {
  const [isWiggling, setIsWiggling] = useState(false);
  const [internalTip, setInternalTip] = useState<string | null>(null);
  const bubbleId = useId();

  // Pick default animation based on pose if not explicitly specified
  const effectiveAnimation: TommyAnimation = animation ?? (() => {
    switch (pose) {
      case 'excited':
        return 'bounce';
      case 'idea':
        return 'glow';
      case 'waving':
        return 'wave';
      case 'proud':
        return 'bob';
      case 'cheerful':
        return 'float';
      default:
        return 'bob';
    }
  })();

  const animationClass = (() => {
    if (isWiggling) return 'animate-tommy-wiggle';
    switch (effectiveAnimation) {
      case 'bob':
        return 'animate-tommy-bob';
      case 'float':
        return 'animate-tommy-float';
      case 'bounce':
        return 'animate-tommy-bounce';
      case 'wave':
        return 'animate-tommy-wave';
      case 'celebrate':
        return 'animate-tommy-celebrate';
      case 'glow':
        return 'animate-tommy-glow';
      case 'wiggle':
        return 'animate-tommy-wiggle';
      case 'none':
      default:
        return '';
    }
  })();

  const src = poseAssets[pose] || poseAssets.happy;

  const sizeStyle = typeof size === 'number'
    ? { width: `${size}px`, height: 'auto' }
    : undefined;

  const sizeClassName = typeof size === 'string'
    ? (sizeMap[size]?.class || sizeMap.md.class)
    : '';

  const displayedBubble = speechBubble ?? internalTip;

  function handleClick() {
    if (interactive) {
      setIsWiggling(true);
      setTimeout(() => setIsWiggling(false), 600);

      if (soundEnabled) {
        playCutePopSound();
      }

      if (!speechBubble) {
        // Pick a random study tip or encouragement
        const randomQuote = TOMMY_STUDY_TIPS[Math.floor(Math.random() * TOMMY_STUDY_TIPS.length)];
        setInternalTip(randomQuote);
        setTimeout(() => setInternalTip(null), 5000);
      }
    }
    onClick?.();
  }

  const bubblePositions: Record<string, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-3',
    'top-right': 'bottom-full left-1/2 -translate-x-1/4 mb-3',
    'top-left': 'bottom-full right-1/2 translate-x-1/4 mb-3',
    right: 'left-full top-1/2 -translate-y-1/2 ml-3',
    left: 'right-full top-1/2 -translate-y-1/2 mr-3',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-3',
  };

  return (
    <div
      className={`relative inline-flex flex-col items-center justify-center select-none ${className}`}
      style={sizeStyle}
    >
      {/* Cartoon Speech Bubble */}
      {displayedBubble && (
        <div
          id={bubbleId}
          role="status"
          className={`absolute ${bubblePositions[bubblePosition] || bubblePositions.top} z-30 min-w-[140px] max-w-[260px] sm:max-w-[300px] p-2.5 px-3.5 bg-white text-on-surface text-xs sm:text-sm font-medium rounded-2xl shadow-xl border border-outline-variant/20 animate-fade-in pointer-events-auto leading-snug`}
        >
          {displayedBubble}
          {/* Bubble tail pointing toward mascot */}
          <span
            className="absolute w-2.5 h-2.5 bg-white border-r border-b border-outline-variant/20 transform rotate-45"
            style={{
              bottom: bubblePosition.startsWith('top') ? '-6px' : undefined,
              top: bubblePosition === 'bottom' ? '-6px' : undefined,
              left: bubblePosition === 'right' ? '-6px' : 'calc(50% - 5px)',
              right: bubblePosition === 'left' ? '-6px' : undefined,
            }}
            aria-hidden="true"
          />
        </div>
      )}

      {/* Mascot Image + Animation Wrapper */}
      <div
        onClick={handleClick}
        className={`relative inline-flex items-center justify-center transition-transform duration-200 ${animationClass} ${
          interactive ? 'cursor-pointer hover:scale-105 active:scale-95' : ''
        }`}
        title={interactive ? 'Click to poke Tommy!' : undefined}
      >
        <img
          src={src}
          alt={alt}
          className={`${sizeClassName} object-contain drop-shadow-md transition-all`}
          draggable={false}
          loading="lazy"
        />
      </div>

      {/* Ground Shadow */}
      {withShadow && (
        <div
          className="w-3/5 h-2 mt-1 rounded-full bg-on-surface/10 blur-[2px] transition-all"
          style={{
            transform: isWiggling ? 'scale(0.85)' : 'scale(1)',
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
